import {
  DatePicker,
  type DatePickerEmpty,
  type DatePickerUnixFrom,
  type DatePickerUnixRange,
  Dropdown,
  type DropdownProps } from
'@sqs/rosetta-compositions';
import { Cell, TextInput, type TextInputProps } from '@sqs/rosetta-elements';
import { Events } from '@sqs/rosetta-icons';
import noop from 'lodash/noop';
import React, { useState } from 'react';
import { useI18n } from '../../i18n';

interface TextInputCellProps {
  readonly setIsOpen: (open: boolean) => void;
  readonly sx?: Record<string, string>;
}

const TextInputCell = React.forwardRef<HTMLElement, TextInputCellProps & TextInputProps>(
  ({ onChange, setIsOpen, value, ...rest }, ref) => {
    const { t } = useI18n();
    return (
      <Cell
        as="label"
        ref={ref}
        body={
        <TextInput
          onChange={onChange}
          onFocus={() => setIsOpen(true)}
          textAlign="right"
          placeholder={t("MM/DD/YYYY", null, { project: 'enterprise-dashboard' })}
          value={value} />

        }
        {...rest} />);


  }
);

interface EditableDropdownProps {
  readonly children: React.ReactNode | React.ReactNode[];
  readonly onRequestClose: (open: boolean) => void;
  readonly onBlur?: () => void;
}

const EditableDropdown = React.forwardRef<HTMLElement, EditableDropdownProps>(
  ({ children, onBlur, onRequestClose, ...rest }, ref) => {
    return (
      <Dropdown
        {...rest}
        ref={ref}
        renderDisplay={TextInputCell}
        renderDropdown={(props: DropdownProps) =>
        <Dropdown.Dropdown
          {...props}
          onBlur={() => {
            onRequestClose(false);
            if (onBlur) {
              onBlur();
            }
          }}
          placement="bottom"
          px={2} />

        }>

        {children}
      </Dropdown>);

  }
);

type DatePickerRange = DatePickerEmpty | DatePickerUnixFrom | DatePickerUnixRange;

interface EditableDropdownGroupProps {
  readonly openDropdownIndex: number | null;
  readonly setOpenDropdownIndex: (index: number | null) => void;
  readonly value: DatePickerRange;

  readonly children?: React.ReactNode | React.ReactNode[];
  readonly onBlur?: () => void;
}

export function EditableDropdownGroup({
  children,
  onBlur,
  openDropdownIndex,
  setOpenDropdownIndex,
  value
}: EditableDropdownGroupProps) {
  const dropdownAnchorRef = React.useRef<HTMLElement>(null);
  const { t, formatDate } = useI18n();

  return (
    <>
      <EditableDropdown
        ref={dropdownAnchorRef}
        // @ts-expect-error
        exteriorPre={<Events />}
        interiorPre={t("Start date", null, { project: 'enterprise-dashboard' })}
        isOpen={openDropdownIndex === 0}
        onBlur={onBlur}
        onRequestClose={() => setOpenDropdownIndex(null)}
        setIsOpen={(open: boolean) => open && setOpenDropdownIndex(0)}
        value={value[0] ? formatDate(value[0], { skeleton: 'yMd' }) : ''}>

        {children}
      </EditableDropdown>
      <EditableDropdown
        ref={dropdownAnchorRef}
        // @ts-expect-error
        exteriorPre={<Events />}
        interiorPre={t("End date", null, { project: 'enterprise-dashboard' })}
        isOpen={openDropdownIndex === 1}
        onBlur={onBlur}
        onRequestClose={() => setOpenDropdownIndex(null)}
        setIsOpen={(open: boolean) => open && setOpenDropdownIndex(1)}
        value={value[1] ? formatDate(value[1], { skeleton: 'yMd' }) : ''}>

        {children}
      </EditableDropdown>
    </>);

}

interface DateRangePickerProps {
  readonly onChange: (value: DatePickerRange) => void;
  readonly value: DatePickerRange;

  readonly onBlur?: () => void;
}

export function DateRangePicker({ onBlur, onChange, value }: DateRangePickerProps) {
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);

  return (
    <EditableDropdownGroup
      onBlur={onBlur || noop}
      openDropdownIndex={openDropdownIndex}
      setOpenDropdownIndex={setOpenDropdownIndex}
      value={value}>

      <DatePicker
        mode={DatePicker.MODE.RANGE}
        onChange={(dateRange: DatePickerRange) => {
          if (openDropdownIndex === 0) {
            setOpenDropdownIndex(1);
          } else if (openDropdownIndex === 1 && dateRange[1]) {
            setOpenDropdownIndex(null);
          }

          onChange(dateRange);
        }}
        value={value} />

    </EditableDropdownGroup>);

}